<template>
  <v-container>
    <v-alert
      v-if="alertMsg.status"
      :timeout="alertMsg.timeout"
      :color="alertMsg.color"
      :type="alertMsg.type"
      dismissible
    >
      {{ alertMsg.text }}
    </v-alert>
    <v-alert v-if="onboardingLoading || declarationLoading" type="info" dense outlined role="status">
      {{ $t("onboarding_loading") }}
    </v-alert>
    <v-alert v-else-if="onboardingError || declarationError" type="error" dense outlined role="alert">
      {{ $t("onboarding_error") }}
      <v-btn small depressed color="primary" class="text-capitalize" @click="refreshDeclaration" :disabled="loader">
        {{ $t("onboarding_refresh") }}
      </v-btn>
    </v-alert>
    <v-alert
      v-else-if="onboardingStatus && unfinishedSections.length"
      color="primary"
      :icon="false"
      dense
      outlined
      class="onboarding-progress"
      role="status"
    >
      <div class="grey--text text--darken-3">
        <div class="onboarding-progress__header">
          <span class="font-weight-bold">{{ $t("onboarding_title") }}</span>
          <v-btn small depressed color="primary" class="text-capitalize" @click="refreshDeclaration" :disabled="loader">
            <v-icon small left>mdi-refresh</v-icon>
            {{ $t("onboarding_refresh") }}
          </v-btn>
        </div>
        <p class="text-caption mt-1 mb-2">
          {{ $t(canSubmitDeclaration ? "onboarding_ready" : "onboarding_pending") }}
        </p>
        <div class="onboarding-progress__group">
          <span class="onboarding-progress__label success--text">
            {{ $t("onboarding_completed") }}
          </span>
          <div v-if="completedSections.length" class="onboarding-progress__sections" role="list">
            <v-chip v-for="section in completedSections" :key="section.key" small outlined color="success" role="listitem">
              <v-icon small left>mdi-check-circle-outline</v-icon>
              {{ $t(section.label) }}
            </v-chip>
          </div>
          <span v-else class="text-caption">{{ $t("onboarding_none") }}</span>
        </div>
        <div class="onboarding-progress__group">
          <span class="onboarding-progress__label error--text">
            {{ $t("onboarding_unfinished") }}
          </span>
          <div v-if="unfinishedSections.length" class="onboarding-progress__sections" role="list">
            <v-chip v-for="section in unfinishedSections" :key="section.key" small outlined color="error" role="listitem">
              <v-icon small left>mdi-alert-circle-outline</v-icon>
              {{ $t(section.label) }}
            </v-chip>
          </div>
          <span v-else class="text-caption">{{ $t("onboarding_none") }}</span>
        </div>
      </div>
    </v-alert>
    <v-row>
      <p class="font-weight-bold">{{ $t("bus_declaration") }}</p>
      <p class="d-flex">
        <v-checkbox
          class="mt-0"
          v-model="addProfilePayload.is_declared"
          :disabled="!canSubmitDeclaration || loader"
          @input="$v.addProfilePayload.is_declared.$touch()"
          :error-messages="applicantErrors"
          hide-details
        ></v-checkbox
        ><span class="mt-1">{{ $t("bus_declaration_text") }}</span>
      </p>
    </v-row>
    <v-row>
      <v-col cols="12" md="12">
        <v-btn
          color="primary"
          class="float-right mt-5 px-5 btn-primary-outlined text-capitalize"
          :disabled="!canSubmitDeclaration || loader"
          :loading="loader"
          @click="onClickNext"
          >{{ $t("Submit") }}
        </v-btn>
      </v-col>
    </v-row>
    <loading
      :active="loader"
      :is-full-page="true"
      :opacity="0.9"
      color="#ff6b00"
    />
  </v-container>
</template>
<script>
import Loading from "vue-loading-overlay";
import AgentService from "@/services/agentService/AgentService";
export default {
  components: { Loading },
  props: {
    isActive: { type: Boolean, default: true },
  },
  data: () => ({
    loader: false,
    onboardingStatus: null,
    onboardingCustomerKey: "",
    onboardingLoading: false,
    onboardingError: false,
    declarationLoading: false,
    declarationError: false,
    onboardingRequestId: 0,
    declarationRequestId: 0,
    onboardingSections: [
      { key: "entity_information", label: "onboarding_entity" },
      { key: "applicant_information", label: "onboarding_representative" },
      { key: "authorization_information", label: "onboarding_management" },
      { key: "upload_documents", label: "onboarding_documents" },
      { key: "other_details", label: "onboarding_other" },
      { key: "business_declaration", label: "declaration" },
    ],
    addProfilePayload: { is_declared: false },
    alertMsg: {
      status: false,
      text: "",
      timeout: 2000,
      color: "blue-grey",
      type: "info",
    },
  }),
  computed: {
    customerKey() {
      return `${this.$route.query.login_id || ""}:${this.$route.query.customer_id || ""}`;
    },
    canSubmitDeclaration() {
      return this.isActive && Boolean(this.$route.query.login_id && this.$route.query.customer_id) &&
        !this.onboardingLoading && !this.declarationLoading &&
        !this.onboardingError && !this.declarationError && !!this.onboardingStatus &&
        this.onboardingCustomerKey === this.customerKey &&
        this.onboardingSections
          .filter((section) => section.key !== "business_declaration")
          .every((section) => this.onboardingStatus[section.key] === true);
    },
    completedSections() {
      return this.onboardingSections.filter(
        (section) => this.onboardingStatus?.[section.key] === true
      );
    },
    unfinishedSections() {
      return this.onboardingSections.filter(
        (section) => this.onboardingStatus?.[section.key] !== true
      );
    },
    applicantErrors() {
      const field = this.$v.addProfilePayload.is_declared;
      return field.$dirty && !field.accepted ? [this.$t("declaration_required")] : [];
    },
  },
  validations() {
    return {
      addProfilePayload: {
        is_declared: { accepted: (value) => value === true },
      },
    };
  },
  watch: {
    customerKey() {
      this.cancelPendingLoads();
      this.addProfilePayload.is_declared = false;
      this.$v.$reset();
      this.refreshDeclaration();
    },
    isActive(active) {
      if (active) this.refreshDeclaration();
      else this.cancelPendingLoads();
    },
  },
  mounted() {
    this.refreshDeclaration();
  },
  beforeDestroy() {
    this.cancelPendingLoads();
  },
  methods: {
    cancelPendingLoads() {
      this.onboardingRequestId++;
      this.declarationRequestId++;
      this.onboardingLoading = false;
      this.declarationLoading = false;
      this.onboardingStatus = null;
      this.onboardingCustomerKey = "";
    },
    refreshDeclaration() {
      if (!this.isActive || !this.$route.query.login_id || !this.$route.query.customer_id) return;
      return Promise.all([this.getdeclaration(), this.getOnboardingStatus()]);
    },
    onClickNext() {
      if (!this.canSubmitDeclaration || this.loader) return;
      this.$v.$touch();
      if (this.$v.$invalid) return;
      return this.postList();
    },
    async getdeclaration() {
      if (!this.isActive || this.declarationLoading || !this.$route.query.login_id || !this.$route.query.customer_id) return;
      const requestId = ++this.declarationRequestId;
      const customerKey = this.customerKey;
      this.declarationLoading = true;
      this.declarationError = false;
      try {
        const response = await AgentService.getDeclarationData({
          login_id: this.$route.query.login_id,
          customer_id: this.$route.query.customer_id,
        });
        if (requestId !== this.declarationRequestId || customerKey !== this.customerKey) return;
        const payload = response?.data;
        if (Number(payload?.status_code) !== 200 || payload?.success === false) {
          throw new Error("Unable to load the saved declaration");
        }
        const declaration = Array.isArray(payload.data) ? payload.data[0] : payload.data;
        this.addProfilePayload.is_declared = declaration?.is_declared === true;
        this.$v.$reset();
      } catch (error) {
        if (requestId === this.declarationRequestId) this.declarationError = true;
      } finally {
        if (requestId === this.declarationRequestId) this.declarationLoading = false;
      }
    },
    async getOnboardingStatus() {
      if (!this.isActive || this.onboardingLoading || !this.$route.query.login_id || !this.$route.query.customer_id) return;
      const requestId = ++this.onboardingRequestId;
      const customerKey = this.customerKey;
      this.onboardingLoading = true;
      this.onboardingError = false;
      this.onboardingStatus = null;
      try {
        const response = await AgentService.getOnboardingStatus({
          login_id: this.$route.query.login_id,
          customer_id: this.$route.query.customer_id,
        });
        if (requestId !== this.onboardingRequestId || customerKey !== this.customerKey) return;
        const payload = response?.data;
        const status = Array.isArray(payload?.data) ? payload.data[0] : payload?.data;
        if (Number(payload?.status_code) !== 200 || payload?.success === false ||
          !status || typeof status !== "object" || Array.isArray(status)) {
          throw new Error("Invalid business onboarding status response");
        }
        this.onboardingStatus = {
          ...status,
          entity_information: status.entity_information ?? status.is_entity_info,
          applicant_information: status.applicant_information ?? status.is_applicant_info,
          upload_documents: status.upload_documents ?? status.is_documents,
          other_details: status.other_details ?? status.is_other_info,
          business_declaration: status.business_declaration ?? status.is_declared,
        };
        this.onboardingCustomerKey = customerKey;
      } catch (error) {
        if (requestId === this.onboardingRequestId) this.onboardingError = true;
      } finally {
        if (requestId === this.onboardingRequestId) this.onboardingLoading = false;
      }
    },
    async postList() {
      if (!this.canSubmitDeclaration || this.loader || this.addProfilePayload.is_declared !== true) return false;
      this.loader = true;
      this.alertMsg.status = false;
      const customerKey = this.customerKey;
      try {
        const response = await AgentService.addDeclarationData(
          { is_declared: true },
          {
            login_id: this.$route.query.login_id,
            customer_id: this.$route.query.customer_id,
            is_declared: true,
          }
        );
        if (customerKey !== this.customerKey) return false;
        if (Number(response?.data?.status_code) !== 200 || response?.data?.success === false) {
          throw new Error(response?.data?.message || "Unable to submit the declaration.");
        }
        this.alertMsg = {
          ...this.alertMsg,
          status: true,
          text: response.data.message || this.$t("declaration_submitted"),
          type: "success",
          color: "green",
        };
        await this.refreshDeclaration();
        return true;
      } catch (error) {
        if (customerKey !== this.customerKey) return false;
        this.alertMsg = {
          ...this.alertMsg,
          status: true,
          text: error?.response?.data?.message || error?.message || this.$t("declaration_submit_error"),
          type: "error",
          color: "error",
        };
        return false;
      } finally {
        this.loader = false;
      }
    },
  },
};
</script>
<style scoped>
.onboarding-progress {
  padding: 10px 12px;
}

.onboarding-progress__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 14px;
}

.onboarding-progress__group {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 6px;
}

.onboarding-progress__label {
  font-size: 12px;
  font-weight: 600;
}

.onboarding-progress__sections {
  display: flex;
  flex: 1 1 240px;
  flex-wrap: wrap;
  gap: 4px;
}
</style>
